import {
  assignRefs,
  cx,
  useAutoResizedTextarea,
  useUniqueDOMId,
} from "@jugl-web/utils";
import {
  forwardRef,
  TextareaHTMLAttributes,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { FormGroupProps } from "../FormGroup";
import { UtilityTextInputBase } from "../UtilityTextInputBase";

const inputTextSize = {
  default: "text-base",
  small: "text-sm",
};

export interface TextAreaInputProps
  extends Pick<
      TextareaHTMLAttributes<HTMLTextAreaElement>,
      | "placeholder"
      | "value"
      | "autoComplete"
      | "onBlur"
      | "onChange"
      | "onKeyDown"
      | "cols"
      | "id"
      | "maxLength"
      | "onFocus"
      | "rows"
    >,
    FormGroupProps {
  classNames?: FormGroupProps["classNames"] & {
    inputClassName?: string;
    inputWrapperClassName?: string;
  };
  size?: keyof typeof inputTextSize;
}

export const TextAreaInput = forwardRef<
  HTMLTextAreaElement,
  TextAreaInputProps
>((props, ref) => {
  const uniqueId = useUniqueDOMId();
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);
  const { textareaProps } = useAutoResizedTextarea(textareaRef);
  const {
    label,
    isInvalid,
    isRequired,
    tooltip,
    errorMessage,
    isDisabled,
    classNames: { inputClassName, inputWrapperClassName } = {},
    size = "default",
    ...htmlInputProps
  } = props;
  const inputId = useMemo(
    () => htmlInputProps.id || uniqueId,
    [htmlInputProps.id, uniqueId]
  );
  const [internalInputValue, setInternalInputValue] = useState<string>(
    typeof htmlInputProps.value === "string" ? htmlInputProps.value : ""
  );
  useEffect(() => {
    if (typeof htmlInputProps.value === "string") {
      setInternalInputValue(htmlInputProps.value);
    }
  }, [htmlInputProps.value]);
  return (
    <UtilityTextInputBase
      {...{
        ...props,
        lengthIndicator: {
          ...props.lengthIndicator,
          current: internalInputValue.length,
        },
      }}
      inputWrapperClassName={inputWrapperClassName}
      inputId={inputId}
    >
      <textarea
        ref={assignRefs([ref, textareaRef])}
        disabled={isDisabled}
        id={inputId}
        rows={1}
        {...htmlInputProps}
        className={cx(
          "h-full w-full flex-1 resize-none rounded-[10px] border-none px-5 py-4 font-secondary leading-4 text-dark outline-none",
          inputTextSize[size],
          inputClassName
        )}
        onChange={(e) => {
          setInternalInputValue(e.target.value || "");
          if (htmlInputProps.onChange) {
            htmlInputProps.onChange(e);
          }
        }}
        {...textareaProps}
      />
    </UtilityTextInputBase>
  );
});
